import React, { useCallback, useState } from 'react';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import classNames from 'classnames';

// Import util modules
import { intlShape, injectIntl, FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';

// Import shared components
import {
  Form,
  CustomExtendedDataField,
  Icons,
  PrimaryButton,
  FieldTextInput,
} from '../../../../components';
// Import modules from this directory
import css from './EditListingDetailsForm.module.css';
import UserTypeSpecificFields from '../../../AuthenticationPage/SignupForm/UserTypeSpecificFields';
import {
  FieldKeys,
  LocationSectionFields,
  TalentSignupStep,
  UserType,
} from '../../../../util/enums';
import TalentFormCard from '../../../AuthenticationPage/SignupForm/TalentFormCard';
import { getPropsForCustomUserFieldInputs } from '../../../../util/userHelpers';
import { insertCustomFields } from '../../../../util/editListingHelpers';
import DefaultUserInfoFields from './DefaultUserInfoFields';
import SocialMediaFields from './SocialMediaFields';
import { divisionSchoolOptions } from '../../../../config/configExtras';
import RenderLocationSections from './RenderLocationSections';
import { debounce } from 'lodash';
import RenderCustomLinks from '../../../AuthenticationPage/SignupForm/RenderCustomLinks';
import FanProfileFormFields from '../../../AuthenticationPage/SignupForm/FanProfileFormFields';
import { emailFormatValid, required } from '../../../../util/validators';
import SchoolInputFormFields from '../../../AuthenticationPage/SignupForm/SchoolInputFormFields';
import AgencyFormInputFields from '../../../AuthenticationPage/SignupForm/AgencyFormInputFields';
import BrandFormFields from '../../../AuthenticationPage/SignupForm/BrandFormFields';
import { getSubmitButtonType } from '../../../../util/genericHelpers';
import DivisionConferenceSchoolFields from './DivisionConferenceSchoolFields';

const MAX_BIO_LENGTH = 350;

// Show various error messages
const ErrorMessage = props => {
  const { fetchErrors } = props;
  const { updateListingError, createListingDraftError, showListingsError } = fetchErrors || {};
  const errorMessage = updateListingError ? (
    <FormattedMessage id="EditListingDetailsForm.updateFailed" />
  ) : createListingDraftError ? (
    <FormattedMessage id="EditListingDetailsForm.createListingDraftError" />
  ) : showListingsError ? (
    <FormattedMessage id="EditListingDetailsForm.showListingFailed" />
  ) : null;

  if (errorMessage) {
    return <p className={css.error}>{errorMessage}</p>;
  }
  return null;
};

// Helper function to get schools based on selected division and conference
export const getSchools = (division, conference) => {
  return divisionSchoolOptions.filter(
    school => school.division === division && school.conference === conference
  );
};

// Helper function to scroll to the first error field
const scrollToFirstError = (errors, formId) => {
  if (!errors || Object.keys(errors).length === 0) return;

  // Get the first error field name
  const firstErrorField = Object.keys(errors)[0];

  // Try to find the element by various selector strategies
  const selectors = [
    `#${formId ? formId + '-' : ''}${firstErrorField}`, // Standard ID
    `[name="${firstErrorField}"]`, // By name attribute
    `input[name="${firstErrorField}"]`, // Input by name
    `textarea[name="${firstErrorField}"]`, // Textarea by name
    `select[name="${firstErrorField}"]`, // Select by name
  ];

  let element = null;
  for (const selector of selectors) {
    element = document.querySelector(selector);
    if (element) break;
  }

  if (element) {
    // Scroll with smooth behavior and offset for better UX
    const yOffset = -100; // Offset to account for fixed headers
    const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;

    window.scrollTo({ top: y, behavior: 'smooth' });

    // Focus the element after a short delay to ensure scroll completes
    setTimeout(() => {
      element.focus();
    }, 300);
  }
};

const EditListingDetailsFormComponent = props => (
  <FinalForm
    keepDirtyOnReinitialize
    {...props}
    mutators={{ ...arrayMutators }}
    render={formRenderProps => {
      const {
        className,
        formId,
        handleSubmit,
        intl,
        updateInProgress,
        fetchErrors,
        values,
        userType,
        listing,
        userFields,
        userTypes,
        form,
        config,
        viewport,
        form: {
          mutators: { push, remove },
        },
        filteredSocialMediaOptions,
        onUpdateInitialStep,
        ready,
        errors,
        submitErrors,
      } = formRenderProps;

      // email
      const emailRequired = required(
        intl.formatMessage({
          id: 'SignupForm.emailRequired',
        })
      );
      const emailValid = emailFormatValid(
        intl.formatMessage({
          id: 'SignupForm.emailInvalid',
        })
      );
      const { FINAL, INITIAL } = TalentSignupStep;
      const { AGENCY, BRAND, FAN, SCHOOL, TALENT } = UserType;

      const { description: initialDescription = '' } = values;

      const [currentStep, setCurrentStep] = useState(INITIAL);
      // Local state to manage the bio
      const [bio, setBio] = useState(initialDescription || '');

      // Handle changes to the bio text
      const handleBioChange = useCallback(
        e => {
          let newValue = e.target.value;

          // If user exceeds 350 chars, truncate to 350
          if (newValue.length > MAX_BIO_LENGTH) {
            newValue = newValue.slice(0, MAX_BIO_LENGTH);
          }

          setBio(newValue);
          form.change('description', newValue); // Sync with form state
        },
        [form]
      );

      // Calculate remaining characters (will not go below 0)
      const remainingChars = Math.max(0, MAX_BIO_LENGTH - bio?.length);

      const classes = classNames(css.root, className);
      const submitInProgress = updateInProgress;
      const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

      const userTypeConfig = userTypes.find(config => config.userType === userType);
      const noUserTypes = !userType && !(userTypes?.length > 0);
      const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;
      const updatedUserFieldProps = insertCustomFields(userFieldProps)?.filter(
        e => !['pub_job_title'].includes(e.key)
      );

      const { firstName, lastName, pub_talent_type, pub_talent_level, phoneNumber } = values || {};

      const locationSectionsFieldProps = updatedUserFieldProps.filter(f =>
        LocationSectionFields.includes(f.key)
      );

      const affiliationSectionsFieldProps = updatedUserFieldProps.filter(
        f => !LocationSectionFields.includes(f.key) && f.key !== FieldKeys.STATE
      );

      const formComponents = {
        [AGENCY]: AgencyFormInputFields,
        [BRAND]: BrandFormFields,
      };

      const SelectedFormFields = formComponents[userType];

      const commonProps = {
        emailFormatValid: emailValid,
        emailRequired,
        intl,
        formId,
        classes,
        handleBioChange,
        remainingChars,
        bio,
        isEditListingDetailsForm: true,
      };

      // Enhanced submit handler with scroll to error
      const handleFormSubmit = useCallback(
        event => {
          const submissionErrors = errors || submitErrors;

          if (submissionErrors && Object.keys(submissionErrors).length > 0) {
            scrollToFirstError(submissionErrors, formId);
          }

          return handleSubmit(event);
        },
        [errors, submitErrors, handleSubmit, formId]
      );

      return (
        <Form className={classes} onSubmit={handleFormSubmit}>
          <ErrorMessage fetchErrors={fetchErrors} />

          {SelectedFormFields && <SelectedFormFields {...commonProps} />}
          <div className={css.defaultUserFields}>
            {![SCHOOL, AGENCY, BRAND].includes(userType) && (
              <DefaultUserInfoFields
                intl={intl}
                userType={userType}
                userTypeConfig={userTypeConfig}
                currentStep={currentStep}
                formId={formId}
                isEditListingDetailsForm={true}
              />
            )}

            {currentStep === FINAL ? (
              <RenderLocationSections
                locationSectionsFieldProps={locationSectionsFieldProps}
                currentStep={currentStep}
                intl={intl}
                formId={formId}
                values={values}
                userType={userType}
                FINAL={FINAL}
                form={form}
              />
            ) : null}
            {currentStep === FINAL ? (
              <h1 className={css.affiliationInfo}>
                {' '}
                {intl.formatMessage({
                  id: 'EditListingDetailsForm.affiliationInfo',
                })}
              </h1>
            ) : null}
            {showCustomUserFields && userType !== FAN ? (
              <div className={css.customFields}>
                {affiliationSectionsFieldProps.map(fieldProps => (
                  <CustomExtendedDataField
                    {...fieldProps}
                    formId={formId}
                    form={form}
                    values={values}
                    currentStep={currentStep}
                    viewport={viewport}
                  />
                ))}
              </div>
            ) : null}

            {userType === SCHOOL && (
              <DivisionConferenceSchoolFields form={form} values={values} intl={intl} />
            )}
            {userType === SCHOOL && <SchoolInputFormFields {...commonProps} />}
            {currentStep === FINAL && (
              <>
                <FieldTextInput
                  className={css.customField}
                  type="textarea"
                  name="description"
                  id="description"
                  label={intl.formatMessage({
                    id: [SCHOOL, BRAND, AGENCY].includes(userType)
                      ? 'EditListingDetailsForm.companyDescription'
                      : 'EditListingDetailsForm.description',
                  })}
                  placeholder={intl.formatMessage({
                    id: 'EditListingDetailsForm.descriptionPlaceholder',
                  })}
                  value={bio}
                  onChange={handleBioChange}
                />
                <div
                  className={classNames(css.characterCount, {
                    [css.warning]: remainingChars < 10 && remainingChars > 0,
                    [css.limitReached]: remainingChars === 0,
                  })}
                >
                  {remainingChars}{' '}
                  {intl.formatMessage({
                    id: 'SignupForm.descriptionCharactersLeft',
                  })}
                </div>
              </>
            )}
            {currentStep === FINAL && <RenderCustomLinks viewport={viewport} />}
            {![BRAND, AGENCY, SCHOOL, FAN].includes(userType) &&
              (userType !== TALENT || currentStep === FINAL) && (
                <TalentFormCard
                  form={form}
                  values={values}
                  push={push}
                  remove={remove}
                  config={config}
                  viewport={viewport}
                  renderSocialMediaFields={
                    <SocialMediaFields
                      form={form}
                      filteredSocialMediaOptions={filteredSocialMediaOptions}
                      values={values}
                      intl={intl}
                      formId={formId}
                    />
                  }
                />
              )}
            {/* {[SCHOOL].includes(userType) && <RenderCustomLinks viewport={viewport} />} */}

            {[BRAND, AGENCY, SCHOOL].includes(userType) && (
              <div className={css.userFlds}>
                <UserTypeSpecificFields
                  userType={userType}
                  form={form}
                  values={values}
                  intl={intl}
                  config={config}
                  isListingPage
                  listing={listing}
                />
              </div>
            )}
            {currentStep === INITIAL && userType === UserType.TALENT && (
              <p className={css.helperText}>
                <FormattedMessage id="EditListingDetailsForm.goNextDetails" />
              </p>
            )}
            {userType === UserType.FAN && (
              <FanProfileFormFields
                intl={intl}
                formId={formId}
                values={values}
                form={form}
                handleBioChange={handleBioChange}
                remainingChars={remainingChars}
                bio={bio}
              />
            )}
          </div>

          <div className={css.bottomWrapper}>
            {userType !== TALENT || currentStep === FINAL}
            <div className={css.formBtns}>
              {currentStep !== INITIAL && (
                <div
                  className={classNames(css.goBack, {
                    [css.pointerEvents]: currentStep === INITIAL,
                  })}
                  onClick={() =>
                    currentStep === FINAL ? setCurrentStep(INITIAL) : history.goBack()
                  }
                >
                  <Icons name="back" />
                  <FormattedMessage id="EditListingDetailsForm.goBack" />
                </div>
              )}

              <PrimaryButton
                className={css.submitBtn}
                onClick={e => {
                  // Prevent any native form submission if needed.
                  if (userType === TALENT && currentStep === INITIAL) {
                    e.preventDefault();
                    const updatedValues = {
                      firstName,
                      lastName,
                      pub_talent_type,
                      pub_talent_level,
                      phoneNumber,
                    };
                    onUpdateInitialStep(updatedValues);
                    setCurrentStep(FINAL);
                  }
                }}
                type={getSubmitButtonType(userType, currentStep, TALENT, INITIAL)}
                inProgress={submitInProgress}
                ready={ready}
              >
                <FormattedMessage
                  id={
                    userType === TALENT && currentStep === INITIAL
                      ? 'EditListingDetailsForm.next'
                      : 'EditListingDetailsForm.save'
                  }
                />
              </PrimaryButton>
            </div>
          </div>
        </Form>
      );
    }}
  />
);

EditListingDetailsFormComponent.defaultProps = {
  className: null,
  formId: 'EditListingDetailsForm',
  fetchErrors: null,
  hasExistingListingType: false,
  listingFieldsConfig: [],
};

EditListingDetailsFormComponent.propTypes = {
  className: string,
  formId: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  onListingTypeChange: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    showListingsError: propTypes.error,
    updateListingError: propTypes.error,
  }),
  pickSelectedCategories: func.isRequired,
  selectableListingTypes: arrayOf(
    shape({
      listingType: string.isRequired,
      transactionProcessAlias: string.isRequired,
      unitType: string.isRequired,
    })
  ).isRequired,
  hasExistingListingType: bool,
  listingFieldsConfig: propTypes.listingFields,
};

export default compose(injectIntl)(EditListingDetailsFormComponent);
